import Layout from "@/Components/Layout/Screen";
import RequiredAuth from "@/Components/RequiredAuth";
import RequiredUnauth from "@/Components/RequiredUnauth";
import { useLoggedUserStore } from "@/Store";
import { lazy, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

const ForgotPassword = lazy(
  () => import("@/Modules/Auth/ForgotPassword/Screen")
);
const Login = lazy(() => import("@/Modules/Auth/Login/Screen"));
const SignUp = lazy(() => import("@/Modules/Auth/SignUp/Screen"));
const ResetPassword = lazy(() => import("@/Modules/Auth/ResetPassword/Screen"));
const ContactCreator = lazy(() => import("@/Modules/Contacts/ContactCreator"));
const ContactsDetail = lazy(() => import("@/Modules/Contacts/ContactDetail"));
const ContactsManagement = lazy(
  () => import("@/Modules/Contacts/ContactManagement")
);
const CustomerCreator = lazy(
  () => import("@/Modules/Customers/CustomerCreator/Screen")
);
const CustomerDetail = lazy(
  () => import("@/Modules/Customers/CustomerDetail/Screen")
);
const CustomerManagement = lazy(
  () => import("@/Modules/Customers/CustomerManagement/Screen")
);
const DepartmentCreator = lazy(
  () => import("@/Modules/Departments/DepartmentCreator")
);
const DepartmentDetail = lazy(
  () => import("@/Modules/Departments/DepartmentDetail")
);
const DepartmentsManagement = lazy(
  () => import("@/Modules/Departments/DepartmentsManagement/Screen")
);
const Home = lazy(() => import("@/Modules/Home/Screen"));
const Overview = lazy(() => import("@/Modules/Overview/Screen"));
const NoMatch = lazy(() => import("@/Modules/NoMatch/Screen"));
const Profile = lazy(() => import("@/Modules/Profile/Screen"));
const DocumentsManagement = lazy(
  () => import("@/Modules/Documents/DocumentsManagement")
);
const ProjectCreator = lazy(() => import("@/Modules/Projects/ProjectCreator"));
const ProjectDetail = lazy(() => import("@/Modules/Projects/ProjectDetail"));
const ProjectManagement = lazy(
  () => import("@/Modules/Projects/ProjectManagement")
);
const UserDetail = lazy(() => import("@/Modules/Users/UserDetail/Screen"));
const UsersManagement = lazy(
  () => import("@/Modules/Users/UsersManagement/Screen")
);
const CompanyManagement = lazy(
  () => import("@/Modules/Companies/CompanyManagement")
);
const CompanyCreator = lazy(() => import("@/Modules/Companies/CompanyCreator"));
const CompanyDetail = lazy(() => import("@/Modules/Companies/CompanyDetail"));
const AccountManagement = lazy(
  () => import("@/Modules/Accounts/AccountManagement")
);
const AccountDetail = lazy(() => import("@/Modules/Accounts/AccountDetail"));
const VacationCreator = lazy(
  () => import("@/Modules/Vacations/VacationCreator")
);
const VacationDetail = lazy(() => import("@/Modules/Vacations/VacationDetail"));
const VacationManagement = lazy(
  () => import("@/Modules/Vacations/VacationManagement")
);
const SharedView = lazy(() => import("@/Modules/Shared/SharedView"));
const LeadsBoard = lazy(() => import("@/Modules/Leads/LeadsBoard"));
const SettingsScreen = lazy(() => import("@/Modules/Settings/Screen"));
const SubscriptionsManagement = lazy(
  () => import("@/Modules/Subscriptions/SubscriptionsManagement")
);
const FinanceOverview = lazy(() => import("@/Modules/Finance/FinanceOverview"));
const InvoicesManagement = lazy(
  () => import("@/Modules/Invoices/InvoicesManagement")
);
const InvoiceCreator = lazy(() => import("@/Modules/Invoices/InvoiceCreator"));
const InvoiceDetail = lazy(() => import("@/Modules/Invoices/InvoiceDetail"));
const KanbanBoards = lazy(() => import("@/Modules/Kanban/KanbanBoards"));
const KanbanBoardView = lazy(() => import("@/Modules/Kanban/KanbanBoardView"));
const AuditManagement = lazy(() => import("@/Modules/Audit/AuditManagement"));
const NotificationsManagement = lazy(
  () => import("@/Modules/Notifications/NotificationsManagement")
);
const MyDay = lazy(() => import("@/Modules/MyDay"));
const BillingScreen = lazy(() => import("@/Modules/Billing"));

export default function App() {
  const loggedUser = useLoggedUserStore((state) => state.loggedUser);
  const isPlatformStaff =
    loggedUser?.type === "superadmin" || loggedUser?.type === "support";
  return (
    <div>
      <Suspense fallback={<div role="status">Carregando página…</div>}>
        <Routes>
          {/* Rota pública de compartilhamento (sem login). */}
          <Route path="/s/:token" element={<SharedView />} />

          <Route element={<RequiredAuth />}>
            <Route path="/" element={<Layout />}>
              <Route index element={<Navigate to="/visao-geral" replace />} />
              <Route path="/inicio" element={<Home />} />
              <Route path="/visao-geral" element={<Overview />} />
              <Route path="/perfil" element={<Profile />} />
              <Route path="/meu-dia" element={<MyDay />} />
              <Route
                path="/notificacoes"
                element={<NotificationsManagement />}
              />
              <Route path="/documentos" element={<DocumentsManagement />} />

              <Route path="/usuarios" element={<UsersManagement />} />
              <Route path="/usuarios/:id" element={<UserDetail />} />
              <Route
                path="/departamentos"
                element={<DepartmentsManagement />}
              />
              <Route
                path="/departamentos/:id"
                element={<DepartmentDetail />}
              />

              <Route path="/contatos" element={<ContactsManagement />} />
              <Route path="/criar-contato" element={<ContactCreator />} />
              <Route path="/contatos/:id" element={<ContactsDetail />} />

              <Route path="/clientes" element={<CustomerManagement />} />
              <Route path="/criar-cliente" element={<CustomerCreator />} />
              <Route path="/clientes/:id" element={<CustomerDetail />} />

              <Route path="/leads" element={<LeadsBoard />} />

              <Route path="/projetos" element={<ProjectManagement />} />
              <Route path="/criar-projeto" element={<ProjectCreator />} />
              <Route path="/projetos/:id" element={<ProjectDetail />} />

              <Route path="/assinantes" element={<SubscriptionsManagement />} />
              <Route path="/financeiro" element={<FinanceOverview />} />
              <Route path="/faturas" element={<InvoicesManagement />} />
              <Route path="/faturas/nova" element={<InvoiceCreator />} />
              <Route path="/faturas/:id" element={<InvoiceDetail />} />

              <Route path="/kanban" element={<KanbanBoards />} />
              <Route path="/kanban/:id" element={<KanbanBoardView />} />

              <Route path="/ferias" element={<VacationManagement />} />
              <Route path="/criar-ferias" element={<VacationCreator />} />
              <Route path="/ferias/:id" element={<VacationDetail />} />

              {["superadmin", "owner", "admin"].includes(
                loggedUser?.type ?? ""
              ) && (
                <>
                  <Route
                    path="/criar-departamento"
                    element={<DepartmentCreator />}
                  />

                  <Route path="/configuracoes" element={<SettingsScreen />} />

                  <Route path="/auditoria" element={<AuditManagement />} />
                </>
              )}

              {(isPlatformStaff || loggedUser?.type === "owner") && (
                <>
                  <Route path="/empresas" element={<CompanyManagement />} />
                  <Route path="/empresas/:id" element={<CompanyDetail />} />
                  {(loggedUser?.type === "owner" ||
                    loggedUser?.type === "superadmin") && (
                    <Route path="/criar-empresa" element={<CompanyCreator />} />
                  )}
                </>
              )}
              {isPlatformStaff && (
                <>
                  <Route path="/contas" element={<AccountManagement />} />
                  <Route path="/contas/:id" element={<AccountDetail />} />
                </>
              )}
              {loggedUser?.type === "owner" && (
                <Route path="/plano" element={<BillingScreen />} />
              )}
              <Route path="*" element={<NoMatch />} />
            </Route>
          </Route>

          <Route element={<RequiredUnauth />}>
            <Route path="/login" element={<Login />} />
            <Route path="/cadastro" element={<SignUp />} />
            <Route path="/redefinir-senha" element={<ForgotPassword />} />
            <Route path="/resetar-senha" element={<ResetPassword />} />
          </Route>
        </Routes>
      </Suspense>
    </div>
  );
}
